// Booth detail / edit. Tapping a booth in the list opens this: every field is
// editable and Save bumps updated_at (via updateBooth) so the edit re-syncs and
// wins last-write-wins on other devices. Photos are shown (local bytes rebuilt
// to Blobs, remote-only ones fetched from their blob_url) and new photos can be
// added — mirrors the capture screen's compress→store path.

import { PRIORITIES, type Show, type Priority } from '../config';
import type { BoothRecord, PhotoRef } from '../db/schema';
import { photoBlob } from '../db/schema';
import { getBooth, getPhotosForBooth, updateBooth, deleteBooth, deleteLocalPhoto, type DraftPhoto } from '../db/booths';
import { compressToStoredPhoto } from '../capture/photo';
import { fetchPhoto } from '../sync/api';
import { syncNow } from '../sync/syncEngine';
import { toast } from '../ui/toast';
import { confirmDialog } from '../ui/confirm';
import { populateShowSelect } from '../ui/showSelect';
import { markThumbNote } from '../ui/thumbNote';
import { openLightbox, type LightboxPhoto } from './lightbox';

interface PendingPhoto {
  draft: DraftPhoto;
  objectUrl: string;
  /** v1.1 per-photo note, held here until this photo's ref is saved onto the booth. */
  note: string;
}

export async function renderDetail(root: HTMLElement, id: string, onDone: () => void): Promise<void> {
  const booth = await getBooth(id);
  // Hide tombstoned records here too (a pulled delete may have landed): treat a
  // deleted booth as gone rather than opening a stale editable copy.
  if (!booth || booth.deleted) {
    root.innerHTML = `<div class="empty">Booth not found.<br/><a href="#/booths" class="link">Back to Booths</a></div>`;
    return;
  }

  root.innerHTML = `
    <form id="edit-form" class="card" novalidate>
      <div class="detail__head">
        <button type="button" id="back" class="link">‹ Booths</button>
        <span class="badge badge--${booth.sync_status}">${booth.sync_status === 'local' ? 'on device' : 'synced'}</span>
      </div>

      <label class="field">
        <span>Show</span>
        <select name="show" class="control" id="show-select">
          <option value="${attr(booth.show)}" selected>${esc(booth.show)}</option>
        </select>
      </label>

      <label class="field">
        <span>Booth # <em>required</em></span>
        <input name="booth_number" class="control" inputmode="text" autocapitalize="characters"
               value="${attr(booth.booth_number)}" required />
      </label>

      <label class="field">
        <span>Vendor name</span>
        <input name="vendor_name" class="control" value="${attr(booth.vendor_name)}" placeholder="Vendor / brand" />
      </label>

      <div class="field">
        <span>Photos</span>
        <div id="thumbs" class="thumbs"></div>
        <label class="btn btn--photo">
          + Add photo
          <input id="photo-input" type="file" accept="image/*" capture="environment" multiple hidden />
        </label>
      </div>

      <div class="field">
        <span>Priority</span>
        <div class="segmented" id="priority">
          ${PRIORITIES.map(
            (p) =>
              `<button type="button" class="seg ${p === booth.priority ? 'seg--on' : ''}" data-priority="${p}">${p}</button>`,
          ).join('')}
        </div>
      </div>

      <label class="field field--row">
        <span>Samples taken</span>
        <input name="samples_taken" type="checkbox" class="switch" ${booth.samples_taken ? 'checked' : ''} />
      </label>

      <label class="field">
        <span>Summary</span>
        <input name="summary" class="control" value="${attr(booth.summary)}" placeholder="One line: what they sell" />
      </label>

      <label class="field">
        <span>Ships from</span>
        <input name="ships_from" class="control" value="${attr(booth.ships_from ?? '')}" placeholder="City / country / warehouse" />
      </label>

      <label class="field">
        <span>Vendor contact</span>
        <input name="vendor_contact" class="control" value="${attr(booth.vendor_contact)}" placeholder="Name / email / phone" />
      </label>

      <label class="field">
        <span>Notes</span>
        <textarea name="notes" class="control" rows="3" placeholder="Anything worth remembering">${esc(booth.notes)}</textarea>
      </label>

      <details class="more"${hasMoreDetails(booth) ? ' open' : ''}>
        <summary class="more__summary"><span class="more__summary-text">More details</span></summary>
        <div class="more__body">
          <label class="field">
            <span>Case packs</span>
            <input name="case_packs" class="control" value="${attr(booth.case_packs ?? '')}" placeholder="e.g. 12 per case" />
          </label>
          <label class="field">
            <span>Size run</span>
            <input name="size_run" class="control" value="${attr(booth.size_run ?? '')}" placeholder="e.g. S–XXL" />
          </label>
          <label class="field">
            <span>Total spent</span>
            <input name="total_spent" class="control" value="${attr(booth.total_spent ?? '')}" placeholder="e.g. $1,200" />
          </label>
        </div>
      </details>

      <div class="detail__meta">Captured by ${esc(booth.captured_by || '—')} · ${esc(new Date(booth.captured_at).toLocaleString())}</div>

      <button type="submit" class="btn btn--primary btn--save">Save changes ✓</button>
      <button type="button" id="delete-booth" class="btn btn--danger">Delete booth</button>
    </form>
  `;

  // Opening a booth from halfway down a long list used to land you halfway down the
  // form (the document keeps its offset across a hash render). Start at the top —
  // the list's own offset is remembered separately and restored on the way back.
  window.scrollTo(0, 0);

  const form = root.querySelector<HTMLFormElement>('#edit-form')!;
  const thumbs = root.querySelector<HTMLDivElement>('#thumbs')!;
  const photoInput = root.querySelector<HTMLInputElement>('#photo-input')!;
  const showSelect = root.querySelector<HTMLSelectElement>('#show-select')!;
  let priority: Priority = booth.priority;
  const pending: PendingPhoto[] = [];

  root.querySelector('#back')!.addEventListener('click', onDone);

  // Controlled show dropdown (same vocabulary as capture; current value preselected).
  void populateShowSelect(showSelect, booth.show);

  // Remove a SAVED photo: drop its PhotoRef, delete local bytes, bump updated_at,
  // and sync. Merge is whole-record last-write-wins, so the shortened photos[]
  // propagates to the backend + other devices — no separate photo tombstone.
  async function removeExistingPhoto(localId: string, wrap: HTMLElement): Promise<void> {
    const ok = await confirmDialog({
      title: 'Remove this photo?',
      message: 'It will be removed from this booth everywhere on the next sync.',
      confirmLabel: 'Remove',
    });
    if (!ok) return;
    booth!.photos = booth!.photos.filter((r) => r.local_id !== localId);
    await deleteLocalPhoto(localId);
    await updateBooth(booth!); // bumps updated_at + flips to unsynced
    wrap.remove();
    toast('Photo removed ✓ (will re-sync)');
    void syncNow();
  }

  // Delete the whole booth (tombstone → hidden locally, propagates on sync).
  root.querySelector('#delete-booth')!.addEventListener('click', async () => {
    const ok = await confirmDialog({
      title: 'Delete this booth?',
      message: 'It will be removed from all devices and can’t be undone.',
      confirmLabel: 'Delete',
    });
    if (!ok) return;
    await deleteBooth(booth!.id);
    toast('Booth deleted ✓');
    void syncNow();
    onDone(); // back to the list, booth gone
  });

  /** The note currently on a photo — from the saved ref, or a not-yet-saved photo. */
  function noteFor(localId: string): string {
    const ref = booth!.photos.find((r) => r.local_id === localId);
    if (ref) return ref.note ?? '';
    return pending.find((p) => p.draft.stored.local_id === localId)?.note ?? '';
  }

  /**
   * Write an edited note back. A photo already on the record persists immediately
   * (updateBooth → re-syncs, so the note reaches other devices); a pending photo
   * keeps it in memory until Save writes its ref. Either way the thumbnail's dot
   * updates so the grid shows which photos are annotated.
   */
  async function saveNote(localId: string, note: string): Promise<void> {
    const wrap = Array.from(thumbs.querySelectorAll<HTMLElement>('.thumb')).find(
      (w) => w.dataset.localId === localId,
    );
    if (wrap) markThumbNote(wrap, !!note);
    const ref = booth!.photos.find((r) => r.local_id === localId);
    if (ref) {
      ref.note = note || undefined;
      await updateBooth(booth!); // bumps updated_at + flips to unsynced
      return;
    }
    const p = pending.find((q) => q.draft.stored.local_id === localId);
    if (p) p.note = note;
  }

  // Tap any thumbnail → fullscreen viewer. Delegated so it also covers photos
  // added after load. Gather the image set (with its blob URLs + photo identity)
  // at open-time in display order — includes both local-rebuilt and remote-fetched
  // photos. The identity is what lets the viewer read/write the right note.
  thumbs.addEventListener('click', (e) => {
    const thumb = (e.target as HTMLElement).closest<HTMLElement>('.thumb');
    if (!thumb) return;
    const wraps = Array.from(thumbs.querySelectorAll<HTMLElement>('.thumb'));
    const idx = wraps.indexOf(thumb);
    if (idx < 0) return;
    const items: LightboxPhoto[] = wraps.map((w) => {
      const localId = w.dataset.localId ?? '';
      return {
        url: w.querySelector('img')?.src ?? '',
        localId,
        note: noteFor(localId),
      };
    });
    openLightbox(items, idx, { onNoteSave: saveNote });
  });

  // Priority segmented control.
  root.querySelectorAll<HTMLButtonElement>('#priority .seg').forEach((btn) => {
    btn.addEventListener('click', () => {
      root.querySelectorAll('#priority .seg').forEach((b) => b.classList.remove('seg--on'));
      btn.classList.add('seg--on');
      priority = btn.dataset.priority as Priority;
    });
  });

  // Existing photos: local bytes rebuilt to Blobs; remote-only fetched by blob_url.
  // Each gets a ✕ that removes the SAVED photo (through the confirm dialog).
  const storedPhotos = await getPhotosForBooth(booth.id);
  for (const p of storedPhotos) {
    const wrap = appendThumbUrl(thumbs, URL.createObjectURL(photoBlob(p)), p.local_id);
    markThumbNote(wrap, !!noteFor(p.local_id));
    addRemoveBtn(wrap, () => removeExistingPhoto(p.local_id, wrap));
  }
  const localIds = new Set(storedPhotos.map((p) => p.local_id));
  await Promise.all(
    booth.photos
      .filter((r) => !localIds.has(r.local_id) && r.blob_url)
      .map(async (r) => {
        try {
          const wrap = appendThumbUrl(
            thumbs,
            URL.createObjectURL(await fetchPhoto(r.blob_url!)),
            r.local_id,
          );
          markThumbNote(wrap, !!(r.note ?? ''));
          addRemoveBtn(wrap, () => removeExistingPhoto(r.local_id, wrap));
        } catch {
          /* signed-out / offline — skip */
        }
      }),
  );

  // Add new photos (same compress→store path as capture). A pending (not-yet-saved)
  // photo can also be removed — pre-save, so just drop it from `pending`.
  photoInput.addEventListener('change', async () => {
    const files = Array.from(photoInput.files ?? []);
    photoInput.value = '';
    for (const file of files) {
      try {
        const stored = await compressToStoredPhoto(file, booth.id, 'product', new Date().toISOString());
        const objectUrl = URL.createObjectURL(photoBlob(stored));
        pending.push({ draft: { stored }, objectUrl, note: '' });
        const wrap = appendThumbUrl(thumbs, objectUrl, stored.local_id);
        addRemoveBtn(wrap, async () => {
          const ok = await confirmDialog({ title: 'Remove this photo?', confirmLabel: 'Remove' });
          if (!ok) return;
          URL.revokeObjectURL(objectUrl);
          const idx = pending.findIndex((pp) => pp.objectUrl === objectUrl);
          if (idx >= 0) pending.splice(idx, 1);
          wrap.remove();
        });
      } catch (err) {
        console.error(err);
        toast('Photo failed — try again', 'warn');
      }
    }
  });

  form.addEventListener('submit', async (e) => {
    e.preventDefault();
    const data = new FormData(form);
    const boothNumber = String(data.get('booth_number') ?? '').trim();
    if (!boothNumber) {
      toast('Booth # is required', 'warn');
      return;
    }

    const newRefs: PhotoRef[] = pending.map((p) => ({
      local_id: p.draft.stored.local_id,
      kind: p.draft.stored.kind,
      blob_url: null,
      synced: false,
      note: p.note || undefined,
    }));

    const updated: BoothRecord = {
      ...booth,
      show: String(data.get('show') ?? booth.show) as Show,
      booth_number: boothNumber,
      vendor_name: String(data.get('vendor_name') ?? '').trim(),
      priority,
      summary: String(data.get('summary') ?? '').trim(),
      samples_taken: data.get('samples_taken') === 'on',
      vendor_contact: String(data.get('vendor_contact') ?? '').trim(),
      // v0.6: the Category input is gone from this form. Carry the record's existing
      // tags through UNCHANGED — reading the (now absent) field would silently wipe
      // real data to [] on every edit. (`...booth` already preserves it; spelled out
      // here so a future edit can't quietly reintroduce the read.)
      category_tags: booth.category_tags,
      notes: String(data.get('notes') ?? '').trim(),
      ships_from: String(data.get('ships_from') ?? '').trim(),
      case_packs: String(data.get('case_packs') ?? '').trim(),
      size_run: String(data.get('size_run') ?? '').trim(),
      total_spent: String(data.get('total_spent') ?? '').trim(),
      photos: [...booth.photos, ...newRefs],
    };

    try {
      await updateBooth(updated, pending.map((p) => p.draft));
      pending.forEach((p) => URL.revokeObjectURL(p.objectUrl));
      toast('Saved ✓ (will re-sync)');
      void syncNow(); // re-push the edit; other devices get it on their next pull
      onDone();
    } catch (err) {
      console.error('updateBooth failed', err);
      const detail = err instanceof Error ? `${err.name}: ${err.message}` : String(err);
      toast(`Save failed (${detail})`, 'warn');
    }
  });
}

function appendThumbUrl(container: HTMLElement, url: string, localId: string): HTMLElement {
  const wrap = document.createElement('div');
  wrap.className = 'thumb';
  // The photo identity rides on the element: the lightbox reads it to know which
  // PhotoRef a note belongs to, and saveNote reads it back to update the dot.
  wrap.dataset.localId = localId;
  wrap.innerHTML = `<img src="${url}" alt="booth photo" />`;
  container.appendChild(wrap);
  return wrap;
}

/** Add a ✕ remove affordance to a thumbnail. Stops the click from also opening
 *  the fullscreen lightbox (which is a delegated handler on the thumbs row). */
function addRemoveBtn(wrap: HTMLElement, onRemove: () => void | Promise<void>): void {
  const x = document.createElement('button');
  x.type = 'button';
  x.className = 'thumb__x';
  x.setAttribute('aria-label', 'Remove photo');
  x.textContent = '✕';
  x.addEventListener('click', (e) => {
    e.preventDefault();
    e.stopPropagation();
    void onRemove();
  });
  wrap.appendChild(x);
}

/** Open the "More details" section by default when any of its fields are filled.
 *  (Ships from now lives outside the section, so it's not a trigger.) */
function hasMoreDetails(b: BoothRecord): boolean {
  return !!(b.case_packs || b.size_run || b.total_spent);
}

function attr(s: string): string {
  return esc(s);
}

function esc(s: string | null | undefined): string {
  return String(s ?? '').replace(/[&<>"']/g, (c) =>
    ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' })[c]!,
  );
}
